import React, { useState } from 'react';
import { Checkbox, Radio } from 'syngenta-digital-cropwise-react-ui-kit';
import FilterChevron from 'screens/Property/components/FilterChevron';
import useTagsData from 'hooks/useTagsData';
import { TagChip } from 'components/TagsRow';
import CropIconMapping from 'components/CropIconMapping';
import {
  CropZoneFilters,
  FilterFarm,
  PropertyFiltersConfig,
  getPropertyFilterOptions
} from 'screens/Property/PropertiesLanding/helpers/propertyFilters';

interface Option {
  id: string;
  name?: string;
  color?: string;
}

interface Props {
  value: CropZoneFilters;
  onChange: (value: CropZoneFilters) => void;
  crops: Option[];
  tags: Option[];
  tagsLoading?: boolean;
}

const FilterSection = ({
  title,
  children,
  initiallyOpen = false
}: {
  title: string;
  children: React.ReactNode;
  initiallyOpen?: boolean;
}) => {
  const [open, setOpen] = useState(initiallyOpen);
  return (
    <section className="crop-zone-filter-section border-b border-neutral-100">
      <button
        type="button"
        className="property-filter-section-header flex justify-between items-center w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-60 focus-visible:ring-inset"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <span className="text-black font-semibold">{title}</span>
        <FilterChevron open={open} />
      </button>
      {open && <div className="px-4 pb-5 flex flex-col gap-2">{children}</div>}
    </section>
  );
};

const toggleId = (ids: string[], id: string) =>
  ids.includes(id) ? ids.filter(item => item !== id) : [...ids, id];

const CropZoneFiltersPanel = ({
  value,
  onChange,
  crops,
  tags,
  tagsLoading = false
}: Props) => {
  const changeField = (key: 'withCropZones' | 'withoutCropZones') => {
    const next = {
      ...value,
      [key]: !value[key]
    };
    if (next.withoutCropZones && !next.withCropZones) {
      next.cropIds = [];
      next.tagIds = [];
      next.tagMatch = 'anyTags';
    }
    onChange(next);
  };
  return (
    <>
      <FilterSection
        title="Fields"
        initiallyOpen={value.withCropZones || value.withoutCropZones}
      >
        <Checkbox
          className="property-filter-option crop-zone-field-option"
          checked={value.withCropZones}
          onChange={() => changeField('withCropZones')}
        >
          With crop zones
        </Checkbox>
        <Checkbox
          className="property-filter-option crop-zone-field-option"
          checked={value.withoutCropZones}
          onChange={() => changeField('withoutCropZones')}
        >
          Without crop zones
        </Checkbox>
      </FilterSection>
      <FilterSection title="Crops" initiallyOpen={value.cropIds.length > 0}>
        {crops.length ? (
          crops.map(crop => (
            <Checkbox
              key={crop.id}
              className="property-filter-option crop-zone-crop-option"
              checked={value.cropIds.includes(crop.id)}
              onChange={() =>
                onChange({
                  ...value,
                  cropIds: toggleId(value.cropIds, crop.id)
                })
              }
            >
              <span className="flex items-center gap-2">
                <span
                  aria-hidden="true"
                  className="crop-zone-filter-crop-icon flex items-center justify-center shrink-0"
                >
                  <CropIconMapping cropObject={crop} size={18} />
                </span>
                <span>{crop.name}</span>
              </span>
            </Checkbox>
          ))
        ) : (
          <p className="text-neutral-60">
            No crops found for the selected season.
          </p>
        )}
      </FilterSection>
      <FilterSection title="Tags" initiallyOpen={value.tagIds.length > 0}>
        {tags.length ? (
          <>
            <span className="text-neutral-60">
              Show crop zones that contain:
            </span>
            <Radio.Group
              value={value.tagMatch}
              onChange={(event: {
                target: { value: CropZoneFilters['tagMatch'] };
              }) => onChange({ ...value, tagMatch: event.target.value })}
              className="crop-zone-tags-match flex flex-col gap-4 mt-3 mb-6"
            >
              <Radio value="anyTags">Any of the selected tags</Radio>
              <Radio value="allTags">All selected tags</Radio>
            </Radio.Group>
            {tags.map(tag => (
              <Checkbox
                key={tag.id}
                className="property-filter-option crop-zone-tag-option"
                checked={value.tagIds.includes(tag.id)}
                onChange={() =>
                  onChange({ ...value, tagIds: toggleId(value.tagIds, tag.id) })
                }
              >
                <TagChip
                  tag={{
                    id: tag.id,
                    name: tag.name ?? '',
                    color: tag.color ?? ''
                  }}
                  truncate
                />
              </Checkbox>
            ))}
          </>
        ) : (
          <p className="text-neutral-60">
            {tagsLoading ? 'Loading tags...' : 'No tags found.'}
          </p>
        )}
      </FilterSection>
    </>
  );
};

export default CropZoneFiltersPanel;

export const PropertyCropZoneFilters = ({
  farms,
  config,
  value,
  onChange
}: {
  farms: FilterFarm[];
  config: PropertyFiltersConfig;
  value: CropZoneFilters;
  onChange: Props['onChange'];
}) => {
  const { tags, loading, catalogueLoaded } = useTagsData();
  const options = getPropertyFilterOptions(
    farms,
    config,
    catalogueLoaded ? tags : undefined
  );
  return (
    <CropZoneFiltersPanel
      value={value}
      onChange={onChange}
      crops={options.crops}
      tags={options.tags}
      tagsLoading={loading && !(value.withoutCropZones && !value.withCropZones)}
    />
  );
};
